import React, { useState } from 'react';
import { X, Sun, Moon, Monitor, KeyRound, Trash2, Plus, ShieldCheck, MonitorSmartphone, ArrowUp, ArrowDown, Grid3X3, Ruler, Keyboard, MousePointer2, Info, User, BookOpenCheck } from 'lucide-react';
import { changeCredentials } from '../store/auth';
import { useCadStore } from '../store/cadStore';
import { THEMES, setThemePreference } from '../theme';
import TrainingPanel from './TrainingPanel';

const PROVIDERS = [
    { id: 'default', label: 'Ollama (Local)', keyRequired: false, defaultModel: 'qwen2.5-coder:7b' },
    { id: 'openai', label: 'OpenAI', keyRequired: true, defaultModel: 'gpt-4o' },
    { id: 'anthropic', label: 'Anthropic', keyRequired: true, defaultModel: 'claude-sonnet-4-20250514' },
    { id: 'groq', label: 'Groq', keyRequired: true, defaultModel: 'llama-3.3-70b-versatile' },
];

export default function SettingsModal({ onClose }) {
    const [tab, setTab] = useState('appearance');
    const t = THEMES.dark;
    return (
        <div onClick={onClose} style={{
            position: 'fixed', inset: 0, zIndex: 9000, background: t.overlay,
            display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16,
            backdropFilter: 'blur(6px)', WebkitBackdropFilter: 'blur(6px)', fontFamily: "'Inter', system-ui, sans-serif",
        }}>
            <div onClick={(e) => e.stopPropagation()} style={{
                width: 640, maxWidth: '100%', maxHeight: '86vh', display: 'flex', flexDirection: 'column',
                background: t.bg, border: `1px solid ${t.borderLight}`, borderRadius: 20, boxShadow: t.shadow, overflow: 'hidden',
            }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 20px', borderBottom: `1px solid ${t.border}` }}>
                    <h2 style={{ margin: 0, fontSize: 16, fontWeight: 800, color: t.text }}>Settings</h2>
                    <button onClick={onClose} style={iconBtn(t)}><X size={18} /></button>
                </div>
                <div style={{ display: 'flex', gap: 4, padding: '12px 20px 0', flexWrap: 'wrap' }}>
                    {[['appearance', 'Appearance', Sun], ['keys', 'API Keys', KeyRound], ['workspace', 'Workspace', Grid3X3], ['training', 'AI Training', BookOpenCheck], ['app', 'App Behavior', MonitorSmartphone], ['account', 'Account', User]].map(([id, label, Icon]) => (
                        <button key={id} onClick={() => setTab(id)} style={{
                            display: 'flex', alignItems: 'center', gap: 6, padding: '8px 14px', borderRadius: 10,
                            border: 'none', cursor: 'pointer', fontSize: 12.5, fontWeight: 600,
                            background: tab === id ? t.accentSoft : 'transparent',
                            color: tab === id ? '#60a5fa' : t.textMuted,
                        }}><Icon size={14} />{label}</button>
                    ))}
                </div>
                <div style={{ flex: 1, overflowY: 'auto', padding: 20 }}>
                    {tab === 'appearance' && <AppearanceTab t={t} />}
                    {tab === 'keys' && <KeysTab t={t} />}
                    {tab === 'workspace' && <WorkspaceTab t={t} />}
                    {tab === 'training' && <TrainingPanel t={t} />}
                    {tab === 'app' && <AppTab t={t} />}
                    {tab === 'account' && <AccountTab t={t} />}
                </div>
            </div>
        </div>
    );
}

const Stack = ({ children }) => <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>{children}</div>;
const iconBtn = (t) => ({ padding: 8, background: 'transparent', border: 'none', cursor: 'pointer', color: t.textMuted, borderRadius: 8, display: 'flex' });
const miniBtn = (t) => ({ padding: '6px 8px', background: t.inputBg, border: `1px solid ${t.borderLight}`, borderRadius: 7, color: t.textSub, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 5, fontSize: 11, fontWeight: 600 });
const selectStyle = (t) => ({ padding: '8px 12px', background: t.surface, border: `1px solid ${t.borderLight}`, borderRadius: 9, color: t.text, fontSize: 12.5, outline: 'none' });
const inputStyle = (t) => ({ flex: 1, padding: '9px 12px', background: t.inputBg, border: `1px solid ${t.borderLight}`, borderRadius: 9, color: t.text, fontSize: 12.5, outline: 'none', minWidth: 0 });
const noteStyle = (t) => ({ display: 'flex', gap: 10, alignItems: 'flex-start', padding: 14, borderRadius: 12, background: t.accentSoft, border: `1px solid ${t.borderLight}`, fontSize: 12.5, color: t.textSub, lineHeight: 1.5 });
const h3Style = (t) => ({ margin: '8px 0 0', fontSize: 11, fontWeight: 800, color: t.textMuted, letterSpacing: '0.1em', textTransform: 'uppercase' });

function Row({ label, hint, children }) {
    const t = THEMES.dark;
    return (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 14, flexWrap: 'wrap' }}>
            <div>
                <div style={{ fontSize: 13, fontWeight: 600, color: t.text }}>{label}</div>
                {hint && <div style={{ fontSize: 11.5, color: t.textMuted, marginTop: 2 }}>{hint}</div>}
            </div>
            {children}
        </div>
    );
}

function Toggle({ label, hint, icon: Icon, on, onChange, t }) {
    return (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 14, padding: '12px 14px', background: t.surface, border: `1px solid ${t.borderLight}`, borderRadius: 12 }}>
            <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
                <div style={{ width: 30, height: 30, borderRadius: 9, background: t.accentSoft, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    <Icon size={15} color={t.accent} />
                </div>
                <div>
                    <div style={{ fontSize: 13, fontWeight: 600, color: t.text }}>{label}</div>
                    {hint && <div style={{ fontSize: 11.5, color: t.textMuted, marginTop: 2, maxWidth: 380 }}>{hint}</div>}
                </div>
            </div>
            <button onClick={() => onChange(!on)} style={{
                width: 44, height: 24, borderRadius: 99, border: 'none', cursor: 'pointer', position: 'relative',
                background: on ? t.accent : t.borderLight, transition: 'background 0.15s', flexShrink: 0,
            }}>
                <span style={{
                    position: 'absolute', top: 3, left: on ? 23 : 3, width: 18, height: 18, borderRadius: '50%',
                    background: '#fff', transition: 'left 0.15s', boxShadow: '0 1px 4px rgba(0,0,0,0.3)',
                }} />
            </button>
        </div>
    );
}

/* ---------- Appearance ---------- */
function AppearanceTab({ t }) {
    const theme = useCadStore((s) => s.settings.theme);
    const updateSetting = useCadStore((s) => s.updateSetting);
    const units = useCadStore((s) => s.settings.units);
    return (
        <Stack>
            <Row label="Theme" hint="Light, Dark, or follow your operating system">
                <div style={{ display: 'flex', background: t.surface, borderRadius: 10, padding: 4, gap: 4 }}>
                    {[['light', Sun], ['dark', Moon], ['system', Monitor]].map(([id, Icon]) => (
                        <button key={id} onClick={() => { updateSetting('theme', id); setThemePreference(id); }} style={{
                            display: 'flex', alignItems: 'center', gap: 6, padding: '7px 14px', borderRadius: 8, border: 'none',
                            cursor: 'pointer', fontSize: 12, fontWeight: 600,
                            background: theme === id ? t.segActiveBg : 'transparent',
                            color: theme === id ? t.segActiveText : t.textMuted,
                        }}><Icon size={13} />{id[0].toUpperCase() + id.slice(1)}</button>
                    ))}
                </div>
            </Row>
            <Row label="Units" hint="Measurement unit for dimensions and export">
                <select value={units} onChange={(e) => updateSetting('units', e.target.value)} style={selectStyle(t)}>
                    <option value="mm">Millimeters (mm)</option>
                    <option value="in">Inches (in)</option>
                </select>
            </Row>
        </Stack>
    );
}

/* ---------- API Keys ---------- */
function KeysTab({ t }) {
    const apiKeys = useCadStore((s) => s.apiKeys);
    const apiModels = useCadStore((s) => s.apiModels);
    const setApiKey = useCadStore((s) => s.setApiKey);
    const removeApiKey = useCadStore((s) => s.removeApiKey);
    const setApiModel = useCadStore((s) => s.setApiModel);
    const settings = useCadStore((s) => s.settings);
    const updateSetting = useCadStore((s) => s.updateSetting);
    const [newKey, setNewKey] = useState({});

    const order = settings.fallbackOrder || [];
    const move = (i, dir) => {
        const next = [...order];
        const j = i + dir;
        if (j < 0 || j >= next.length) return;
        [next[i], next[j]] = [next[j], next[i]];
        updateSetting('fallbackOrder', next);
    };

    return (
        <Stack>
            <div style={noteStyle(t)}>
                <ShieldCheck size={16} color={t.success} style={{ flexShrink: 0 }} />
                <span>Keys are stored locally in your browser only. If a provider fails, RooCAD automatically falls back to the next provider in the priority list below.</span>
            </div>

            <h3 style={h3Style(t)}>Fallback Priority</h3>
            {order.map((pid, i) => {
                const p = PROVIDERS.find((x) => x.id === pid) || { label: pid, keyRequired: true };
                const hasKey = !!apiKeys[pid];
                return (
                    <div key={pid} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px', background: t.surface, border: `1px solid ${t.borderLight}`, borderRadius: 12 }}>
                        <span style={{ fontSize: 11, color: t.textMuted, fontWeight: 700, width: 18 }}>#{i + 1}</span>
                        <span style={{ flex: 1, fontSize: 13, fontWeight: 600, color: t.text }}>{p.label}</span>
                        <span style={{ fontSize: 10, fontWeight: 700, padding: '3px 8px', borderRadius: 99, background: hasKey || !p.keyRequired ? 'rgba(34,197,94,0.15)' : t.dangerSoft, color: hasKey || !p.keyRequired ? t.success : t.danger }}>
                            {p.keyRequired ? (hasKey ? 'KEY SET' : 'NO KEY') : 'LOCAL'}
                        </span>
                        <button onClick={() => move(i, -1)} disabled={i === 0} style={miniBtn(t)}><ArrowUp size={13} /></button>
                        <button onClick={() => move(i, 1)} disabled={i === order.length - 1} style={miniBtn(t)}><ArrowDown size={13} /></button>
                    </div>
                );
            })}

            <h3 style={h3Style(t)}>Manage Keys</h3>
            {PROVIDERS.filter((p) => p.keyRequired).map((p) => (
                <div key={p.id} style={{ padding: 14, background: t.surface, border: `1px solid ${t.borderLight}`, borderRadius: 12, display: 'flex', flexDirection: 'column', gap: 10 }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <strong style={{ fontSize: 13, color: t.text }}>{p.label}</strong>
                        {apiKeys[p.id] && (
                            <button onClick={() => removeApiKey(p.id)} style={{ ...miniBtn(t), color: t.danger, borderColor: t.danger }}><Trash2 size={13} /> Remove key</button>
                        )}
                    </div>
                    <div style={{ display: 'flex', gap: 8 }}>
                        <input type="password" placeholder={apiKeys[p.id] ? '••••••••••••••••' : `Paste ${p.label} API key…`}
                            value={newKey[p.id] || ''} onChange={(e) => setNewKey((k) => ({ ...k, [p.id]: e.target.value }))}
                            style={inputStyle(t)} />
                        <button onClick={() => { if (newKey[p.id]?.trim()) { setApiKey(p.id, newKey[p.id].trim()); setNewKey((k) => ({ ...k, [p.id]: '' })); } }}
                            style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '8px 14px', borderRadius: 9, border: 'none', cursor: 'pointer', background: t.accent, color: '#fff', fontSize: 12, fontWeight: 700 }}>
                            <Plus size={13} /> Save
                        </button>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <span style={{ fontSize: 11, color: t.textMuted, whiteSpace: 'nowrap' }}>Model</span>
                        <input value={apiModels[p.id] || p.defaultModel} onChange={(e) => setApiModel(p.id, e.target.value)} style={inputStyle(t)} />
                    </div>
                </div>
            ))}

            <h3 style={h3Style(t)}>Local Ollama</h3>
            <div style={{ padding: 14, background: t.surface, border: `1px solid ${t.borderLight}`, borderRadius: 12, display: 'flex', flexDirection: 'column', gap: 10 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span style={{ fontSize: 11, color: t.textMuted, whiteSpace: 'nowrap' }}>Endpoint</span>
                    <input value={apiModels.ollamaUrl || 'http://45.248.77.206:11434'} onChange={(e) => setApiModel('ollamaUrl', e.target.value)} style={inputStyle(t)} />
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span style={{ fontSize: 11, color: t.textMuted, whiteSpace: 'nowrap' }}>Model</span>
                    <input value={apiModels.ollamaModel || 'qwen2.5-coder:7b'} onChange={(e) => setApiModel('ollamaModel', e.target.value)} style={inputStyle(t)} />
                </div>
            </div>
        </Stack>
    );
}
/* ---------- Workspace ---------- */
function WorkspaceTab({ t }) {
    const s = useCadStore((st) => st.settings);
    const update = useCadStore((st) => st.updateSetting);
    return (
        <Stack>
            <Toggle label="Keyboard Shortcuts" hint="Del delete · Ctrl+D duplicate · Ctrl+Z / Ctrl+Shift+Z undo/redo · M / R / S gizmo modes · Esc deselect" icon={Keyboard} on={s.keyboardShortcuts} onChange={(v) => update('keyboardShortcuts', v)} t={t} />
            <Toggle label="Right-Click Context Menu" hint="Right-click objects in the viewport for quick actions (duplicate, delete, export)" icon={MousePointer2} on={s.rightClickMenu} onChange={(v) => update('rightClickMenu', v)} t={t} />
            <Toggle label="Snap to Grid" hint="Movement snaps to the step size below" icon={Grid3X3} on={s.snap} onChange={(v) => update('snap', v)} t={t} />
            <Row label="Snap Step" hint="Grid snapping increment (scene units)">
                <input type="number" step="0.1" min="0.1" value={s.snapStep} onChange={(e) => update('snapStep', parseFloat(e.target.value) || 0.5)} style={{ ...inputStyle(t), width: 90 }} />
            </Row>
            <Row label="Workplane Grid" hint="Show the grid in the 3D viewport">
                <select value={s.gridVisible ? 'on' : 'off'} onChange={(e) => update('gridVisible', e.target.value === 'on')} style={selectStyle(t)}>
                    <option value="on">Visible</option>
                    <option value="off">Hidden</option>
                </select>
            </Row>
            <Row label="Print Bed X×Y (mm)" hint="Maximum print dimensions — parts larger than this will be flagged">
                <div style={{ display: 'flex', gap: 6 }}>
                    <input type="number" min="10" value={s.printBed?.x ?? 220} onChange={(e) => update('printBed', { ...(s.printBed || { y: 220, z: 250 }), x: parseFloat(e.target.value) || 220 })} style={{ ...inputStyle(t), width: 64 }} />
                    <input type="number" min="10" value={s.printBed?.y ?? 220} onChange={(e) => update('printBed', { ...(s.printBed || { x: 220, z: 250 }), y: parseFloat(e.target.value) || 220 })} style={{ ...inputStyle(t), width: 64 }} />
                    <input type="number" min="10" value={s.printBed?.z ?? 250} onChange={(e) => update('printBed', { ...(s.printBed || { x: 220, y: 220 }), z: parseFloat(e.target.value) || 250 })} style={{ ...inputStyle(t), width: 64 }} />
                </div>
            </Row>
            <div style={noteStyle(t)}>
                <Ruler size={16} color={t.accent} style={{ flexShrink: 0 }} />
                <span>Advanced mode adds precision position/scale inputs, snap gizmo modes and a SketchUp-style measurement workflow. Simple mode keeps the Tinkercad drag-and-drop feel.</span>
            </div>
        </Stack>
    );
}

/* ---------- Account ---------- */
function AccountTab({ t }) {
    const email = JSON.parse(localStorage.getItem('roocad_session') || '{}').email || '';
    const [newEmail, setNewEmail] = useState('');
    const [pw, setPw] = useState('');
    const [confirm, setConfirm] = useState('');
    const [msg, setMsg] = useState(null);
    const [logoutFn, setLogoutFn] = useState(null);
    React.useEffect(() => { setLogoutFn(window.__roocad_logout || null); }, []);
    const save = () => {
        if (pw && pw !== confirm) return setMsg({ ok: false, text: 'Passwords do not match.' });
        if (pw && pw.length < 8) return setMsg({ ok: false, text: 'Password must be at least 8 characters.' });
        const r = changeCredentials(email, newEmail.trim() || null, pw || null);
        if (r.ok) { setMsg({ ok: true, text: 'Account updated.' }); if (newEmail && newEmail !== email) setLogoutFn(true); }
        else setMsg({ ok: false, text: r.error });
    };
    const inp = { width: '100%', padding: '9px 10px', background: t.inputBg, border: `1px solid ${t.borderLight}`, borderRadius: 8, color: t.text, fontSize: 13, outline: 'none', boxSizing: 'border-box' };
    return (
        <Stack>
            <Row label="Signed in as" hint="Your current RooCAD account">
                <span style={{ color: t.text, fontSize: 12.5, fontWeight: 600 }}>{email}</span>
            </Row>
            <Row label="Change Email" hint="Leave blank to keep your current email">
                <input type="email" placeholder="new@email.com" value={newEmail} onChange={(e) => setNewEmail(e.target.value)} style={{ ...inp, width: 220 }} />
            </Row>
            <Row label="New Password" hint="Leave blank to keep your current password">
                <input type="password" placeholder="••••••••" value={pw} onChange={(e) => setPw(e.target.value)} style={{ ...inp, width: 220 }} />
            </Row>
            <Row label="Confirm Password" hint="Repeat the new password">
                <input type="password" placeholder="••••••••" value={confirm} onChange={(e) => setConfirm(e.target.value)} style={{ ...inp, width: 220 }} />
            </Row>
            {msg && <div style={{ fontSize: 12, padding: '8px 12px', borderRadius: 8, background: msg.ok ? 'rgba(34,197,94,0.1)' : 'rgba(239,68,68,0.12)', border: `1px solid ${msg.ok ? 'rgba(34,197,94,0.3)' : 'rgba(239,68,68,0.35)'}`, color: msg.ok ? '#4ade80' : '#f87171' }}>
                {msg.text}{msg.ok && newEmail ? ' Re-login will be required with your new email.' : ''}
            </div>}
            <button onClick={save} style={{ alignSelf: 'flex-start', padding: '9px 18px', borderRadius: 9, border: 'none', cursor: 'pointer', fontWeight: 700, fontSize: 12.5, color: '#fff', background: 'linear-gradient(135deg, #2563eb, #6366f1)' }}>Save Changes</button>
        </Stack>
    );
}

/* ---------- App Behavior ---------- */
function AppTab({ t }) {
    const s = useCadStore((st) => st.settings);
    const update = useCadStore((st) => st.updateSetting);
    const [installReady, setInstallReady] = useState(!!window.__roocad_install_prompt);
    React.useEffect(() => {
        const fn = () => setInstallReady(true);
        window.addEventListener('roocad-install-available', fn);
        return () => window.removeEventListener('roocad-install-available', fn);
    }, []);
    return (
        <Stack>
            <Toggle label="Require PIN on Launch" hint="Ask for the 4-digit PIN (0884) at the start of each session" icon={ShieldCheck}
                on={s.pinEnabled} onChange={(v) => update('pinEnabled', v)} t={t} />
            <Toggle label="Standalone App Behavior" hint="When installed: removes browser defaults (text selection, browser right-click), enables full right-click context menu and complete keyboard control — feels like native desktop software" icon={MonitorSmartphone}
                on={s.standaloneBehavior} onChange={(v) => update('standaloneBehavior', v)} t={t} />
            <div style={noteStyle(t)}>
                <Info size={16} color={t.accent} style={{ flexShrink: 0 }} />
                <span>
                    <strong>Install RooCAD as an app:</strong> use your browser menu → “Install RooCAD”, or{' '}
                    {installReady ? (
                        <button onClick={() => window.__roocad_install_prompt?.prompt?.()} style={{ color: '#60a5fa', background: 'none', border: 'none', cursor: 'pointer', fontWeight: 700, fontSize: 12.5, padding: 0, textDecoration: 'underline' }}>click here to install now</button>
                    ) : 'look for the install icon in your address bar.'}
                    {' '}Once installed, RooCAD runs in its own window with all standard web-browser behaviors replaced by native CAD controls.
                </span>
            </div>
        </Stack>
    );
}